import { ChainData, ChainType } from "@0xsquid/squid-types";
import type { TransactionReceipt, TransactionResponse } from "ethers";
import {
  AddEthereumChainParameter,
  Address as EvmAddress,
  isAddress as isEvmAddress,
} from "viem";
import { Connector } from "wagmi";
import {
  ConnectorID,
  EvmWallet,
  WindowWalletFlag,
} from "../../core/types/wallet";
import { sleep } from "./transactionService";
import { standardWalletOverrides } from "./walletStandardService";

export const EvmNetworkNotSupportedErrorCode = 4902;

export async function addEthereumChain({
  chain,
  provider,
}: {
  chain: ChainData;
  provider: any;
}) {
  const chainName = chain.rpc.includes("tenderly")
    ? `${chain.networkName} Tenderly fork`
    : chain.networkName;
  const chainParameters: AddEthereumChainParameter = {
    chainId: `0x${parseInt(chain.chainId, 10).toString(16)}`,
    chainName,
    nativeCurrency: chain.nativeCurrency,
    rpcUrls: [chain.rpc],
    blockExplorerUrls: chain.blockExplorerUrls,
    iconUrls: [chain.chainIconURI],
  };

  try {
    return provider.request({
      method: "wallet_addEthereumChain",
      params: [chainParameters],
    });
  } catch (error) {
    console.debug("Error adding chain:", error);
  }
}

export const parseEvmAddress = (
  address: string | undefined
): EvmAddress | undefined => {
  if (address && isEvmAddress(address)) {
    return address as EvmAddress;
  }
};

/**
 * Formats a Wagmi connector into a Squid Wallet
 */
export function formatEvmWallet(connector: Connector): EvmWallet {
  const { icon, name: defaultName, id: wagmiConnectorId } = connector;

  const walletOverrides = standardWalletOverrides[wagmiConnectorId];
  const walletIcon = walletOverrides?.icon || icon;
  const name = walletOverrides?.name || defaultName;
  const connectorId =
    walletOverrides?.connectorId || (wagmiConnectorId as ConnectorID);
  const isNonInstallableWallet = [
    "injected",
    "walletConnect",
    "metaMaskSDK",
    "coinbaseWalletSDK",
  ].includes(wagmiConnectorId);

  return {
    connectorId,
    name,
    connectorName: name,
    type: ChainType.EVM,
    icon: walletIcon,
    rdns: wagmiConnectorId,
    windowFlag: name as WindowWalletFlag,
    // For non-installable wallets, we don't want to show the "Installed" label
    // but still want to allow the user to connect
    skipInstallCheck: isNonInstallableWallet,
    isInstalled: isNonInstallableWallet ? undefined : () => true,
    connector,
    isMobile: true,
  };
}

export function filterWagmiConnector(connector: Connector) {
  // Safe is auto-connected when available, should not be listed as an available wallet
  return connector.id !== "safe";
}

/**
 * Waits for a transaction receipt, handling retries if indexing is in progress
 */
export async function waitForReceiptWithRetry(
  txResponse: TransactionResponse,
  retries = 5,
  delayMs = 2000
): Promise<TransactionReceipt | null> {
  let lastError;

  for (let i = 0; i < retries; i++) {
    try {
      return await txResponse.wait();
    } catch (error: any) {
      lastError = error;
      if (error?.message?.match(/transaction indexing is in progress/i)) {
        await sleep(delayMs);
        continue;
      }
      throw error;
    }
  }

  throw lastError;
}
